import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paperclip } from 'lucide-react';
import { toast } from 'react-toastify';
import { useEPMContext } from '../../contexts/EPMContext';
import { formatTestCardId } from '../../utils/converter';
import { COLUMN_LABELS } from './TestingBoard/constants';
import { STEP_LABELS, fmtDay, iceScore, liveProgress, ownerInitials, ownerName, stepForStatus, structureOf, testId, } from './epmHelpers';
import './TestingBoard/styles.css';

// What a test needs next, by stage — each deep-links into the step that resolves it
const nextStepFor = (test) => {
    const { flag, schedule, result } = structureOf(test);
    switch (test?.status) {
        case 'IDEAS':
            return { title: flag === 'no evidence' ? 'Attach evidence, then score it' : 'Score it and decide', step: flag === 'no evidence' ? 0 : 1 };
        case 'PRIORITISED':
            return { title: 'Set the primary metric and duration', step: 2 };
        case 'PLANNING':
            return { title: 'Finish the plan and clear its gate', step: 2 };
        case 'BUILD_QA':
            return flag === 'sign-off lost'
                ? { title: 'Sign-off invalidated — Experience edited', detail: 'Re-run the production checks against the new version and sign off again.', step: 3 }
                : { title: 'Complete production QA and sign off', step: 3 };
        case 'SCHEDULED':
            return { title: schedule ? `Goes live ${fmtDay(schedule.start_day)}` : 'Waiting for go-live', step: 4 };
        case 'LIVE': {
            if (flag === 'guardrail') return { title: 'Guardrail trending toward breach', detail: 'Step 2 exit rate +0.9pp (Adobe Analytics), within tolerance. Stops automatically on a breach.', step: 4 };
            const progress = liveProgress(test);
            return { title: progress ? `Live · day ${progress.day} of ${progress.total}` : 'Live', step: 4 };
        }
        case 'ANALYSING':
            return { title: flag === 'verdict due' ? 'Ended 2 days ago, no verdict' : 'Settling before the readout', step: 5 };
        case 'CONCLUDED':
            return { title: result ? `Concluded · ${[result.verdict, result.uplift].filter(Boolean).join(' ')}` : 'Concluded', step: 5 };
        default:
            return { title: 'Open the test', step: 0 };
    }
};

const Dt = ({ children }) => <dt className="font-semibold text-[10.5px] leading-relaxed font-mono uppercase tracking-wide text-zinc-400">{children}</dt>;

// Wireframe placeholder, used until a test has captured mockups
const Placeholder = ({ sticky }) => (
    <div className="epm-peek-ph">
        <i className="pic"></i><i></i><i style={{ width: '70%' }}></i>{!sticky && <i className="cta"></i>}<i></i><i style={{ width: '55%' }}></i>{sticky && <i className="cta sticky"></i>}
    </div>
);

const ARMS = [
    { key: 'control', title: 'Control', fallback: 'unmodified page' },
    { key: 'variant', title: 'B', fallback: 'variant' },
];

// Variant screenshots (storyboard 08): real captures when the test has them, else the wireframe
const Shots = ({ mockups }) => {
    const [enlarged, setEnlarged] = useState(false);
    useEffect(() => {
        if (!enlarged) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setEnlarged(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [enlarged]);
    const hasImages = !!(mockups?.control?.src && mockups?.variant?.src);

    return (
        <div className="relative">
            <div className="epm-peek-shots">
                {ARMS.map((arm) => (
                    <div key={arm.key} className="epm-peek-shot">
                        <div className={`epm-peek-img${hasImages ? ' has-shot' : ''}`} onClick={() => hasImages && setEnlarged(true)}>
                            {hasImages
                                ? <img src={mockups[arm.key].src} alt={`${arm.title}: ${mockups[arm.key].label || arm.fallback}`} loading="lazy" />
                                : <Placeholder sticky={arm.key === 'variant'} />}
                            <span className="epm-peek-zoom">⤢ Enlarge</span>
                        </div>
                        <div className="flex items-baseline gap-2 mt-2.5 text-[13px]">
                            <b className="font-semibold text-zinc-900">{arm.title}</b>
                            <span className="text-zinc-500 truncate">{mockups?.[arm.key]?.label || arm.fallback}</span>
                            <span className="ml-auto font-semibold text-[12px] font-mono tracking-wide text-zinc-500">50%</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-3 mt-3 text-[12px] text-zinc-500">
                <span>{hasImages ? `${mockups.device || 'Mobile'} · control captured on ${mockups.site || 'the live site'} · variant mocked up` : 'Mobile · captured mockups'}</span>
                <span className="flex-1"></span>
                <span>Desktop</span><span>·</span>
                <button type="button" onClick={() => hasImages && setEnlarged(true)} className="bg-transparent border-0 p-0 text-zinc-500 cursor-pointer hover:text-zinc-800 transition-colors">Compare side by side</button>
            </div>
            {enlarged && (
                <div className="epm-shot-lightbox" onClick={() => setEnlarged(false)} role="dialog" aria-modal="true" aria-label="Control and variant side by side">
                    <div className="epm-shot-lightbox__row" onClick={(e) => e.stopPropagation()}>
                        {ARMS.map((arm) => (
                            <figure key={arm.key}>
                                <img src={mockups[arm.key].src} alt={`${arm.title}: ${mockups[arm.key].label || arm.fallback}`} />
                                <figcaption><b>{arm.title}</b> · {mockups[arm.key].label || arm.fallback}</figcaption>
                            </figure>
                        ))}
                    </div>
                    <button type="button" className="epm-shot-lightbox__close" onClick={() => setEnlarged(false)}>✕ Close</button>
                </div>
            )}
        </div>
    );
};

// Saved variants are lettered from A (as in Build & QA)
const variantLetter = (index) => String.fromCharCode(65 + index);
const IMAGE_FILE = /\.(png|jpe?g|gif|webp|svg)$/i;
const isImageFile = (variant) => IMAGE_FILE.test(variant?.key || String(variant?.source_url || '').split('?')[0]);

// The test's saved Build & QA variants; two fit side by side, more scroll horizontally
const VariantShots = ({ variants }) => (
    <div>
        <div className="epm-peek-variants">
            {variants.map((variant, index) => (
                <div key={variant._id || variant.key || index} className="epm-peek-shot">
                    <a
                        href={variant.source_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Open file in a new tab"
                        className="epm-peek-img has-shot"
                        style={{ cursor: 'pointer' }}
                    >
                        {isImageFile(variant)
                            ? <img src={variant.source_url} alt={`Variant ${variantLetter(index)}: ${variant.details || ''}`} loading="lazy" />
                            : <span className="flex flex-col items-center gap-2 text-[13px] text-zinc-500"><Paperclip size={20} />Open file</span>}
                    </a>
                    <div className="flex items-baseline gap-2 mt-2.5 text-[13px] min-w-0">
                        <b className="font-semibold text-zinc-900">{variantLetter(index)}</b>
                        <span className="text-zinc-500 truncate" title={variant.details}>{variant.details}</span>
                        <a href={variant.source_url} target="_blank" rel="noreferrer" className="ml-auto flex-none text-[12px] underline text-zinc-700">Open ↗</a>
                    </div>
                </div>
            ))}
        </div>
        <div className="mt-3 text-[12px] text-zinc-500">
            {variants.length} variant{variants.length === 1 ? '' : 's'}{variants.length > 2 ? ' · scroll for more' : ''}
        </div>
    </div>
);

/**
 * The body shared by the test peek (storyboard 08) and the full test page:
 * header, variant screenshots, hypothesis and key facts, progress, next step,
 * and the traceability / related / activity sections.
 */
const TestOverview = ({ testData, setTestData, actions, variant = 'peek' }) => {
    const navigate = useNavigate();
    const { tagsMap, updateTest } = useEPMContext();

    const id = testId(testData);
    const structure = structureOf(testData);
    const { plan = {}, schedule, hypothesis } = structure;
    const location = testData?.ideation?.location || {};

    // Production sign-off comes from the build (Build & QA step); 6 gates, the hard conflict is still open
    const production = testData?.build_qa?.production;
    const productionSigned = !!production?.signed_by;
    const gatesMet = productionSigned ? 5 : 4;

    const cardId = formatTestCardId(testData);
    const tags = testData?.tag_ids?.map((tagId) => tagsMap?.[tagId]?.name).filter(Boolean) || [];
    const ice = iceScore(testData);
    const step = stepForStatus(testData?.status);
    const variants = testData?.build_qa?.variants || [];
    const next = nextStepFor(testData);
    const openStep = (stepIndex) => navigate(`/epm/test-workflow/${id}?step=${stepIndex}`);

    const hypothesisText = testData?.ideation?.hypothesis || testData?.description || 'No hypothesis yet.';
    const where = [location.url_pattern, location.funnel_step].filter(Boolean);
    const runs = plan.duration_days
        ? `${plan.duration_days} days · ${plan.stop_rule === 'sequential' ? 'sequential' : 'fixed sample'}${schedule ? ` · ${fmtDay(schedule.start_day)}–${fmtDay(schedule.end_day)}` : ''}`
        : null;

    const headerClass = variant === 'peek' ? 'p-7 px-9 border-b border-zinc-100 relative' : 'p-2 pb-6 border-b border-zinc-100 relative';
    const bodyClass = variant === 'peek' ? 'p-7 px-9 flex flex-col gap-8' : 'pt-8 flex flex-col gap-8';

    return (
        <>
            <div className={headerClass}>
                <div className="flex items-center gap-2 mb-3">
                    <span className="epm-peek-key">{cardId}</span>
                    <span className="inline-block px-2.5 py-0.5 border border-primary rounded-full bg-primary text-white text-[11.5px] ml-1.5 whitespace-nowrap">
                        {COLUMN_LABELS[testData?.status] || 'Ideas'}
                    </span>
                    <span className="flex-1"></span>
                    {actions}
                </div>
                <h3 className="text-[22px] m-0 mb-3.5 tracking-tight leading-tight text-zinc-900 font-medium">
                    {testData?.name}
                </h3>
                <div className="flex items-center gap-2 flex-wrap text-[13px] text-zinc-600">
                    <span className="inline-flex items-center gap-2 border border-zinc-300 rounded-md bg-white py-1 pr-2.5 pl-1 text-[13px] text-zinc-900 shadow-sm">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-zinc-200 text-zinc-700 text-[9px] font-semibold flex-none">
                            {ownerInitials(testData)}
                        </span>
                        {ownerName(testData) || 'Unknown'}
                    </span>
                    {tags.map((tag) => (
                        <span key={tag} className="inline-block px-2.5 py-0.5 border border-zinc-100 rounded-full bg-zinc-100 text-[11.5px] text-zinc-600 whitespace-nowrap">
                            {tag}
                        </span>
                    ))}
                    <span className="flex-1"></span>
                    <span className="text-[12px] text-zinc-400">{[structure.squad && `${structure.squad} squad`, structure.property].filter(Boolean).join(' · ')}</span>
                </div>
            </div>

            <div className={bodyClass}>
                {variants.length ? (
                    <VariantShots variants={variants} />
                ) : step >= 3 ? (
                    <div className="border border-dashed border-zinc-300 rounded-lg p-6 text-center text-[13px] text-zinc-500">
                        No variants yet · add them in Build & QA.
                    </div>
                ) : (
                    <div className="border border-dashed border-zinc-300 rounded-lg p-6 text-center text-[13px] text-zinc-500">
                        Variant screenshots appear here once the test is built in Build & QA.
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-9">
                    <div className="relative">
                        <div className="epm-peek-lbl">Hypothesis</div>
                        <p className="text-[14.5px] text-zinc-800 m-0 leading-relaxed">
                            {hypothesisText || 'No hypothesis yet.'}
                        </p>
                        <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-2.5 mt-5 text-[13px]">
                            <Dt>Measures</Dt>
                            <dd className="m-0 text-zinc-800">{plan.primary_metric ? `${plan.primary_metric} · 2 guardrails` : '—'}</dd>
                            <Dt>Where</Dt>
                            <dd className="m-0 text-zinc-800">{where.length ? <><span className="epm-peek-mono">{where[0]}</span>{where[1] && ` · ${where[1]}`}</> : '—'}</dd>
                            <Dt>Runs</Dt>
                            <dd className="m-0 text-zinc-800">{runs || '—'}</dd>
                            <Dt>Priority</Dt>
                            <dd className="m-0 text-zinc-800">{ice === null ? 'Not scored yet' : `ICE ${ice.toFixed(1)}${testData?.triaged?.status === 'ACCEPT' ? ' · accepted' : ''}`}</dd>
                            {/* <Dt>Platform</Dt>
                            <dd className="m-0 text-zinc-800"><span className="epm-peek-mono">adobe-target</span></dd> */}
                        </dl>
                    </div>

                    <div className="relative">
                        <div className="epm-peek-seg">
                            {STEP_LABELS.map((label, index) => (
                                <i key={label} className={index < step ? 'done' : ''}></i>
                            ))}
                        </div>
                        <div className="flex justify-between items-center mt-2.5 text-[12px] text-zinc-500">
                            <span>Step {step + 1} of 6 · <b className="text-zinc-900 font-semibold">{STEP_LABELS[step]}</b></span>

                        </div>

                        <div className="mt-6 border border-zinc-200 border-l-[3px] border-l-primary rounded-r-lg p-5 bg-white">
                            <div className="epm-peek-lbl">Next step</div>
                            {<>
                                    <p className="text-[15px] font-semibold m-0 mb-1.5 leading-snug text-zinc-900">{next.title}</p>
                                    {next.detail && <p className="text-[13px] text-zinc-600 m-0 mb-4">{next.detail}</p>}
                                    <div className="flex gap-2.5 flex-wrap mt-3">
                                        <button type="button" onClick={() => openStep(next.step)} className="inline-block px-4 py-2 border border-zinc-300 rounded-md bg-white text-[13px] text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors">
                                            Open {STEP_LABELS[next.step]} step →
                                        </button>
                                    </div>
                                </>}
                        </div>
                    </div>
                </div>

                <div className="border-t border-zinc-100">

                    {/* <details className="epm-peek-details flat border-b border-zinc-100">
                        <summary>
                            <span className="t relative">Activity 🔒</span>
                            <span className="s truncate max-w-[300px]">Latest: {testData?.updatedAt && testData.updatedAt !== testData.createdAt ? 'updated' : 'idea captured'}</span>
                            <span className="end">Show</span>
                        </summary>
                        <div className="in pb-6 pt-1">
                            {testData?.updatedAt && testData.updatedAt !== testData.createdAt && (
                                <div className="epm-peek-evt">
                                    <span>{new Date(testData.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase()}</span>
                                    <span>Updated · now at {COLUMN_LABELS[testData.status]}</span>
                                </div>
                            )}
                            <div className="epm-peek-evt">
                                <span>{new Date(testData?.createdAt || Date.now()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase()}</span>
                                <span>Idea captured · {ownerName(testData) || 'Unknown'}</span>
                            </div>
                        </div>
                    </details> */}
                </div>
            </div>
        </>
    );
};

export default TestOverview;
